import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  ArrowPathIcon,
  ChartBarIcon,
  CheckIcon,
  ClockIcon,
  CubeIcon,
  CursorArrowRaysIcon,
  DocumentTextIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  QueueListIcon,
  RectangleStackIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import MarketingNav from '../components/Marketing/MarketingNav';
import MarketingFooter from '../components/Marketing/MarketingFooter';
import usePublicStore, { monthlyFrom } from '../hooks/usePublicStore';
import { convertPrice } from '../utils/currency';
import { DLR_GAMES } from '../utils/dlrGames';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';

// Count up once the number scrolls into view. Starts at 80% of the target so the real
// figure is what crawlers, link previews and no-scroll visitors see.
const useCountUp = (target, { duration = 1200 } = {}) => {
  const [value, setValue] = useState(target ? target * 0.8 : 0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (target) setValue((v) => (started.current ? v : target * 0.8));
    const node = ref.current;
    if (!node || !target) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || started.current) return;
      started.current = true;
      const from = target * 0.8;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / duration);
        setValue(from + (target - from) * (1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.3 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration]);

  return { ref, display: Math.round(value).toLocaleString() };
};

const Metric = ({ target, suffix = '', label }) => {
  const { ref, display } = useCountUp(target);
  return (
    <div ref={ref} className="text-center">
      <div className="text-3xl md:text-5xl font-semibold text-white tabular-nums">{display}{suffix}</div>
      <div className="mt-2 text-sm md:text-base text-gray-400">{label}</div>
    </div>
  );
};

const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
    <p className="text-xs font-semibold uppercase tracking-widest text-primary-400 mb-3">{eyebrow}</p>
    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white">{title}</h2>
    {children && <p className="mt-4 text-gray-400">{children}</p>}
  </div>
);

// A static, coded preview of the exam screen (crisp at any size, no screenshot to keep updated)
const ExamPreview = () => {
  const answers = [
    { l: 'A', t: 'The temperature is increasing' },
    { l: 'B', t: 'The temperature is decreasing', wrong: true },
    { l: 'C', t: 'It first increases, then decreases' },
    { l: 'D', t: 'The temperature is constant', right: true },
  ];
  return (
    <div className="relative" aria-hidden="true">
      <div className="absolute -inset-6 rounded-[2rem] bg-primary-600/20 blur-3xl" />
      <div className="relative rounded-2xl border border-white/10 bg-slate-900/90 backdrop-blur-xl shadow-2xl shadow-black/50 p-5 sm:p-6">
        <div className="flex items-center justify-between text-xs">
          <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 font-medium text-emerald-300">Practice mode</span>
          <span className="inline-flex items-center gap-1.5 font-mono text-gray-400"><ClockIcon className="h-4 w-4" />08:42</span>
        </div>
        <p className="mt-4 text-xs text-gray-500">Meteorology · Question 7 of 20</p>
        <p className="mt-1.5 text-base sm:text-lg font-medium leading-snug text-white">
          What can be said about the temperature in the lower stratosphere?
        </p>
        <div className="mt-4 space-y-2">
          {answers.map((a) => (
            <div
              key={a.l}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm ${
                a.right ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200'
                  : a.wrong ? 'border-rose-500/50 bg-rose-500/10 text-rose-200'
                    : 'border-white/10 text-gray-300'}`}
            >
              <span className="font-semibold">{a.l}.</span>
              <span className="flex-1">{a.t}</span>
              {a.right && <CheckIcon className="h-4 w-4" />}
            </div>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-10 gap-1.5">
          {Array.from({ length: 20 }, (_, i) => (
            <span
              key={i}
              className={`h-2 rounded-full ${i < 6 ? (i === 2 ? 'bg-rose-500/70' : 'bg-emerald-500/70') : i === 6 ? 'bg-primary-500' : 'bg-white/10'}`}
            />
          ))}
        </div>
      </div>
      <div className="absolute -bottom-16 -left-2 sm:-left-8 rounded-xl border border-white/10 bg-slate-900/95 px-4 py-3 shadow-xl">
        <p className="text-[11px] uppercase tracking-wider text-gray-500">Last mock exam</p>
        <p className="mt-0.5 text-lg font-semibold text-white">86% <span className="text-sm font-normal text-emerald-400">passed</span></p>
      </div>
    </div>
  );
};

const NewLandingPage = () => {
  const [stats, setStats] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const { store } = usePublicStore();

  // Live platform metrics; the band is hidden if they can't be loaded (never show made-up numbers)
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/courses/platform-stats/`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (!cancelled) setStats(d.raw || d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const courses = store?.courses || [];
  const subjectCount = courses.reduce((n, c) => n + (c.subject_count || 0), 0);
  const plans = [
    ...courses.map((c) => ({ key: c.code, name: c.code === 'DLR' ? 'DLR & airline screening' : 'ATPL theory', blurb: `${c.subject_count} subjects · ${Number(c.total_questions).toLocaleString()}+ questions`, note: c.code === 'DLR' ? `+ screening games from ${convertPrice(Math.min(...DLR_GAMES.map((g) => g.price)), 'JOD')} each` : null, from: monthlyFrom(c.pricing_tiers) })),
    ...(store?.bundles || []).slice(0, 1).map((b) => ({ key: 'bundle', name: 'All-in-One ATPL + DLR', blurb: 'Both programmes in one plan', from: monthlyFrom(b.pricing_tiers), featured: true })),
  ].filter((p) => p.from);
  // Only call the bundle "best value" when it really is cheaper than buying both programmes
  const singlesTotal = plans.filter((p) => !p.featured).reduce((sum, p) => sum + p.from, 0);
  const bundleIsCheaper = plans.some((p) => p.featured && plans.filter((q) => !q.featured).length === 2 && p.from < singlesTotal);

  const features = [
    { icon: DocumentTextIcon, title: 'Real exam questions', description: 'A bank that grows as new questions are reported from recent sittings.' },
    { icon: ClockIcon, title: 'Real exam mode', description: 'Timed papers in the true format, so the day itself feels familiar.' },
    { icon: ArrowPathIcon, title: 'Learn from mistakes', description: 'Retry just the questions you got wrong, with similar questions grouped.' },
    { icon: ChartBarIcon, title: 'Progress you can see', description: 'Scores by subject and chapter show where to spend your next hour.' },
  ];

  const games = [
    { code: 'SKT', name: 'Concentrativeness', desc: 'Sustained attention under pressure.', icon: EyeIcon },
    { code: 'MIC', name: 'Instrument Coordination', desc: 'Fly heading, altitude and speed together.', icon: CursorArrowRaysIcon },
    { code: 'MEK', name: 'Visual Memory', desc: 'Memorise symbol–number pairs.', icon: RectangleStackIcon },
    { code: 'RMS', name: 'Running Memory Span', desc: 'Recall spoken sequences.', icon: SpeakerWaveIcon },
    { code: 'ROT', name: 'Cube Rotation', desc: 'Track a cube through rotations.', icon: CubeIcon },
    { code: 'OWT', name: 'Perceptual Speed', desc: 'Spot the critical reading fast.', icon: MagnifyingGlassIcon },
    { code: 'PPT', name: 'Paper Folding', desc: 'Fold a net into the right cube.', icon: QueueListIcon },
  ];

  // Keep in sync with the FAQ structured data in src/seo/seoConfig.json ("/")
  const faqs = [
    { q: 'How current is the question database?', a: 'It is continuously updated as new questions are reported from recent exam sittings, so you always practise what is actually being asked right now.' },
    { q: 'Do you cover the DLR / airline screening games?', a: 'Yes. AirBrief includes 7 psychometric assessment games that mirror the real screening tasks — concentration, coordination, memory, spatial rotation and more.' },
    { q: 'How do I pay?', a: 'Pay by CliQ bank transfer in Jordanian dinars, or redeem an access code. Your access starts as soon as the transfer is confirmed.' },
    { q: 'Can I use it on my phone and laptop?', a: 'AirBrief runs in any modern browser on desktop, tablet and mobile, with your progress synced across every device.' },
    { q: 'What if I am not satisfied?', a: 'We stand behind the product. See our refund policy for full details on eligibility and timelines.' },
  ];

  const navLinks = [
    { href: '#features', label: 'Features' },
    { href: '#games', label: 'Screening games' },
    { href: '/pricing', label: 'Pricing' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-gray-200">
      <MarketingNav links={navLinks} />

      {/* Hero */}
      <section className="relative flex min-h-[88vh] items-center overflow-hidden">
        <img
          src={`${API_URL}/media/banners/banner.jpeg`}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-[65%_center]"
        />
        {/* Darken only behind the text and fade into the page below */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950 to-transparent" />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <div className="max-w-xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/20 px-3 py-1 text-xs text-gray-100 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              ATPL theory · DLR airline screening
            </p>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
              Pass your exams.<br />Ace the screening.
            </h1>
            <p className="mt-6 text-lg text-gray-200">
              One study system for ATPL theory and DLR preparation: real questions, real exam mode, and the assessment games airlines use.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="group inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 hover:bg-primary-500 px-7 py-3.5 font-semibold text-white shadow-lg shadow-black/30 transition-colors">
                Get started
                <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to="/pricing" className="inline-flex items-center justify-center rounded-lg border border-white/40 bg-black/10 px-7 py-3.5 font-semibold text-white backdrop-blur-sm hover:bg-white/10 transition-colors">
                View pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Live metrics */}
      {stats && (
        <section className="bg-slate-950 py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-center gap-x-8 gap-y-10 md:gap-x-20 [&>*]:basis-[40%] md:[&>*]:basis-auto">
            {[
              { target: stats.total_questions, suffix: '+', label: 'Real exam questions' },
              { target: stats.exams_taken, suffix: '+', label: 'Practice exams taken' },
              { target: subjectCount, label: 'Subjects across ATPL & DLR' },
              { target: stats.games_count || 7, label: 'Screening games' },
            ].filter((m) => m.target > 0).map((m) => <Metric key={m.label} {...m} />)}
          </div>
        </section>
      )}

      {/* See it in action: the example question + what you get */}
      <section id="features" className="scroll-mt-20 border-t border-white/5 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-400">See it in action</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-white">Practise the way the exam feels</h2>
            <p className="mt-4 text-gray-400">Real questions by subject and chapter, instant feedback, and timed mock exams you can review question by question.</p>
            <ul className="mt-8 space-y-4">
              {features.map((f) => (
                <li key={f.title} className="flex gap-4">
                  <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-600/15 text-primary-300">
                    <f.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-semibold text-white">{f.title}</span>
                    <span className="block text-sm text-gray-400">{f.description}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden px-2 pb-20 pt-6 sm:overflow-visible">
            <div className="mx-auto max-w-md"><ExamPreview /></div>
          </div>
        </div>
      </section>

      {/* Screening games */}
      <section id="games" className="scroll-mt-20 border-t border-white/5 bg-slate-900/60 py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="DLR & airline screening" title="Train the exact assessment games">
            Theory alone won&apos;t get you hired. Practise the 7 psychometric tasks used in real airline selection until they become second nature.
          </SectionHeading>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {games.map((g) => (
              <div key={g.code} className="rounded-2xl border border-white/10 bg-slate-950/60 p-4 md:p-5 transition-colors hover:border-primary-500/40">
                <div className="flex items-center justify-between">
                  <span className="text-base md:text-lg font-bold tracking-wide text-primary-300">{g.code}</span>
                  <g.icon className="h-5 w-5 text-gray-500" />
                </div>
                <h3 className="mt-3 text-sm md:text-base font-semibold text-white">{g.name}</h3>
                <p className="mt-1 text-xs md:text-sm text-gray-400">{g.desc}</p>
              </div>
            ))}
            <Link to="/register" className="flex flex-col justify-center rounded-2xl border border-dashed border-primary-500/40 bg-primary-600/10 p-4 md:p-5 transition-colors hover:bg-primary-600/20">
              <span className="font-semibold text-white">Start training</span>
              <span className="mt-1 inline-flex items-center gap-1 text-sm text-primary-300">All 7 games <ArrowRightIcon className="h-4 w-4" /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing teaser (live prices) */}
      {plans.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Pricing" title="Simple plans, no subscription traps">
              Pay once for the access period you need. Prices shown per month on the longest plan.
            </SectionHeading>
            <div className="grid gap-4 md:grid-cols-3">
              {plans.map((p) => (
                <Link
                  key={p.key}
                  to="/pricing"
                  className={`rounded-2xl border p-6 transition-colors ${p.featured ? 'border-primary-500/60 bg-primary-600/10 hover:bg-primary-600/15' : 'border-white/10 bg-white/[0.03] hover:border-white/25'}`}
                >
                  {p.featured && <span className="mb-3 inline-block rounded-full bg-primary-600 px-2.5 py-0.5 text-xs font-semibold text-white">{bundleIsCheaper ? 'Best value' : 'Both programmes'}</span>}
                  <p className="text-lg font-semibold text-white">{p.name}</p>
                  <p className="mt-1 text-sm text-gray-400">{p.blurb}</p>
                  {p.note && <p className="mt-1 text-sm text-primary-300">{p.note}</p>}
                  <p className="mt-5 text-gray-400">from <span className="text-3xl font-semibold text-white">{convertPrice(p.from, 'JOD')}</span> / month</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary-300">See plans <ArrowRightIcon className="h-4 w-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className="scroll-mt-20 py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Questions" title="Frequently asked" />
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={f.q} className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-center justify-between px-5 py-4 text-left font-medium text-white hover:bg-white/[0.03]"
                >
                  <span>{f.q}</span>
                  <span className={`ml-4 text-xl leading-none text-primary-400 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-sm leading-relaxed text-gray-400">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-primary-500/30 bg-gradient-to-br from-primary-600/25 via-primary-700/10 to-transparent p-8 md:p-14 text-center">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white">Ready to start?</h2>
            <p className="mx-auto mt-4 max-w-xl text-gray-300">Current questions, realistic exams and the exact screening games, all in one place.</p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/register" className="w-full sm:w-auto rounded-lg bg-primary-600 hover:bg-primary-500 px-8 py-3.5 font-semibold text-white transition-colors">Create free account</Link>
              <Link to="/pricing" className="w-full sm:w-auto rounded-lg border border-white/20 px-8 py-3.5 font-semibold text-white hover:bg-white/10 transition-colors">Compare plans</Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default NewLandingPage;
